import { useState, useRef, useEffect } from "react";
import { useLoginModal } from "../components/LoginModalProvider";
import { useNavigate } from "react-router-dom";
import { supabase } from '../lib/supabase';
import { tokens } from '../tokens';
import { useBreakpoint } from '../hooks/useBreakpoint';
import AppLogo from "../components/AppLogo";

// Route: /signup/business
// Add to App.jsx or router file:
//   import BusinessSignUpPage from "./pages/BusinessSignUpPage";
//   <Route path="/signup/business" element={<BusinessSignUpPage />} />
// Local dev: http://localhost:3000/signup/business
//
// Linked from SignUpPage.jsx via "Signing up for a business? Register here"
// (routes to /signup?type=business in that file — update that link to
// /signup/business to point here, or keep both patterns and redirect
// internally, whichever matches your router setup).
//
// AUTH MODEL: identical phone-only Supabase Phone Auth flow as
// SignUpPage.jsx (signInWithOtp -> verifyOtp -> updateUser -> profiles
// upsert), with an additional `businesses` table row created on success
// and role set to 'business'.
//
// ON SUCCESS: navigates to /business/settings (Business Client Settings
// page — dashboard to be built separately per your note).

const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap";
document.head.appendChild(fontLink);

const F = "'Plus Jakarta Sans', sans-serif";

const BUSINESS_TYPES = [
  { id: "property_management", label: "Property management", icon: "Building2" },
  { id: "corporate",            label: "Corporate / office",   icon: "Briefcase" },
  { id: "hospitality",          label: "Hotel / Airbnb",       icon: "Hotel" },
  { id: "retail_sme",           label: "Retail / SME",         icon: "Store" },
  { id: "other",                label: "Other",                icon: "Grid" },
];

const Icon = ({ name, size = 18, color = "currentColor", strokeWidth = 1.8 }) => {
  const paths = {
    Eye:       <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    EyeOff:    <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><path d="M1 1l22 22"/></>,
    ArrowRight:<><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    ArrowLeft: <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    MessageSquare: <><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></>,
    Building2: <><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 0-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></>,
    Briefcase: <><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
    Hotel:     <><path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z"/><path d="m9 22 .5-4h5l.5 4"/><path d="M6.4 8h11.2"/><path d="M6.4 12h11.2"/><path d="M6.4 16h11.2"/></>,
    Store:     <><path d="M2 7h20l-1.4 8.4a2 2 0 0 1-2 1.6H5.4a2 2 0 0 1-2-1.6L2 7Z"/><path d="M2 7l2-4h16l2 4"/><path d="M10 11v6"/><path d="M14 11v6"/></>,
    Grid:      <><rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/></>,
    Check:     <><polyline points="20 6 9 17 4 12"/></>,
    Shield:    <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    Users:     <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    FileText:  <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

function Field({ label, type = "text", value, onChange, placeholder, error, autoFocus, inputMode }) {
  const [focused, setFocused] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === "password";
  const { isMobile } = useBreakpoint();

  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: isMobile ? 11 : 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6, fontFamily: F, letterSpacing: "-0.005em" }}>
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          type={isPassword && showPw ? "text" : type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          autoFocus={autoFocus}
          inputMode={inputMode}
          style={{
            width: "100%", padding: isMobile ? "11px 14px" : "13px 16px",
            borderRadius: 12,
            border: `1.5px solid ${error ? "#ef4444" : focused ? "#16a34a" : "#e7e9ee"}`,
            fontSize: isMobile ? 13.5 : 14.5, outline: "none", fontFamily: F,
            color: "#111827", background: focused ? "#fff" : "#fafbfc",
            boxSizing: "border-box",
            boxShadow: focused
              ? "0 0 0 4px rgba(22,163,74,0.10), inset 0 1px 2px rgba(0,0,0,0.02)"
              : "inset 0 1px 2px rgba(0,0,0,0.02)",
            transition: "border-color 0.18s, box-shadow 0.18s, background 0.18s",
            paddingRight: isPassword ? (isMobile ? 42 : 46) : (isMobile ? 14 : 16),
            minHeight: isMobile ? tokens.spacing[44] : "auto",
            letterSpacing: "-0.005em",
          }}
        />
        {isPassword && (
          <button type="button" onClick={() => setShowPw(s => !s)}
            style={{ position: "absolute", right: isMobile ? 12 : 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 0, display: "flex", minHeight: isMobile ? tokens.spacing[44] : "auto" }}>
            <Icon name={showPw ? "EyeOff" : "Eye"} size={isMobile ? 16 : 18} strokeWidth={1.8} />
          </button>
        )}
      </div>
      {error && <p style={{ fontSize: 12, color: "#ef4444", margin: "6px 0 0 2px", fontFamily: F }}>{error}</p>}
      {isPassword && !error && <p style={{ fontSize: 11, color: "#9ca3af", margin: "5px 0 0 2px", fontFamily: F }}>At least 8 characters.</p>}
    </div>
  );
}

function OtpInput({ value, onChange, error, isMobile }) {
  const refs = useRef([]);
  const digits = value.split("").concat(Array(6 - value.length).fill(""));

  const setDigit = (i, d) => {
    const next = digits.slice();
    next[i] = d;
    const joined = next.join("").slice(0, 6);
    onChange(joined);
    if (d && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(text);
    refs.current[Math.min(text.length, 5)]?.focus();
  };

  return (
    <div>
      <div style={{ display: "flex", gap: isMobile ? 8 : 10, justifyContent: "center" }} onPaste={handlePaste}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={el => (refs.current[i] = el)}
            value={d}
            onChange={e => setDigit(i, e.target.value.replace(/\D/g, "").slice(-1))}
            onKeyDown={e => handleKeyDown(i, e)}
            inputMode="numeric"
            maxLength={1}
            autoFocus={i === 0}
            style={{
              width: isMobile ? 42 : 50, height: isMobile ? 50 : 58,
              textAlign: "center", fontSize: isMobile ? 20 : 24, fontWeight: 700,
              borderRadius: 13, border: `1.5px solid ${error ? "#ef4444" : d ? "#16a34a" : "#e7e9ee"}`,
              outline: "none", fontFamily: F, color: "#111827",
              background: d ? "#fff" : "#fafbfc",
              boxShadow: d
                ? "0 0 0 4px rgba(22,163,74,0.10), inset 0 1px 2px rgba(0,0,0,0.02)"
                : "inset 0 1px 2px rgba(0,0,0,0.02)",
              transition: "border-color 0.18s, box-shadow 0.18s, background 0.18s",
            }}
          />
        ))}
      </div>
      {error && <p style={{ fontSize: 12, color: "#ef4444", margin: "10px 0 0", fontFamily: F, textAlign: "center" }}>{error}</p>}
    </div>
  );
}

/* ── Business type selector ── */
function BusinessTypeGrid({ value, onChange, isMobile }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ fontSize: isMobile ? 11 : 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 8, fontFamily: F, letterSpacing: "-0.005em" }}>
        Business type
      </label>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {BUSINESS_TYPES.map(bt => {
          const active = value === bt.id;
          return (
            <button key={bt.id} type="button" onClick={() => onChange(bt.id)}
              style={{
                display: "flex", alignItems: "center", gap: 9,
                padding: "11px 13px", borderRadius: 12,
                border: `1.5px solid ${active ? "#16a34a" : "#e7e9ee"}`,
                background: active ? "rgba(22,163,74,0.07)" : "#fafbfc",
                cursor: "pointer", transition: "all 0.15s", textAlign: "left",
              }}>
              <div style={{
                width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                background: active ? "rgba(22,163,74,0.14)" : "#fff",
                border: `1px solid ${active ? "rgba(22,163,74,0.25)" : "#e7e9ee"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Icon name={bt.icon} size={14} color={active ? "#16a34a" : "#9ca3af"} strokeWidth={1.8} />
              </div>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: active ? "#111827" : "#4b5563", fontFamily: F, lineHeight: 1.3 }}>{bt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function BusinessSignUpPage() {
  const navigate = useNavigate();
  const { open: openLogin } = useLoginModal();
  const { isMobile, isTablet } = useBreakpoint();

  const [step, setStep] = useState("details"); // 'details' -> 'otp'

  const [companyName, setCompanyName] = useState("");
  const [contactName, setContactName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const normalisePhone = (p) => {
    const digits = p.replace(/[^0-9]/g, "");
    if (p.trim().startsWith("+")) return "+" + digits;
    if (digits.startsWith("254")) return "+" + digits;
    if (digits.startsWith("0")) return "+254" + digits.slice(1);
    return "+254" + digits;
  };

  const validateDetails = () => {
    const e = {};
    if (!companyName.trim()) e.companyName = "Required";
    if (!contactName.trim()) e.contactName = "Required";
    if (!businessType) e.businessType = "Select a business type";
    if (!phone.trim()) e.phone = "Phone number required";
    else if (phone.replace(/[^0-9]/g, "").length < 9) e.phone = "Enter a valid phone number";
    if (password.length < 8) e.password = "At least 8 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const formattedPhone = normalisePhone(phone);

  const handleSendOtp = async (ev) => {
    ev.preventDefault();
    if (!validateDetails()) return;
    setLoading(true);
    setErrors({});
    try {
      const { error } = await supabase.auth.signInWithOtp({ phone: formattedPhone });
      if (error) throw error;
      setResendCooldown(30);
      setStep("otp");
    } catch (err) {
      setErrors({ general: err.message || "Could not send verification code. Please check your number and try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({ phone: formattedPhone });
      if (error) throw error;
      setResendCooldown(30);
    } catch (err) {
      setErrors({ otp: err.message || "Could not resend code." });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (ev) => {
    ev.preventDefault();
    if (otp.length < 6) {
      setErrors({ otp: "Enter the 6-digit code" });
      return;
    }
    setLoading(true);
    setErrors({});

    try {
      const { data, error } = await supabase.auth.verifyOtp({
        phone: formattedPhone,
        token: otp,
        type: "sms",
      });
      if (error) throw error;
      if (!data.user) throw new Error("Verification failed. Please try again.");

      const { error: updateError } = await supabase.auth.updateUser({
        password,
        data: { full_name: contactName, phone: formattedPhone, role: "business" },
      });
      if (updateError) throw updateError;

      const { error: profileError } = await supabase
        .from("profiles")
        .upsert({
          id: data.user.id,
          full_name: contactName,
          phone: formattedPhone,
          role: "business",
        });
      if (profileError) throw profileError;

      const { error: businessError } = await supabase
        .from("businesses")
        .upsert({
          owner_id: data.user.id,
          name: companyName,
          business_type: businessType,
        }, { onConflict: "owner_id" });
      if (businessError) throw businessError;

      navigate("/business/account");
    } catch (err) {
      setErrors({ otp: err.message || "Invalid or expired code. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const leftPadding = isMobile ? "24px 24px" : (isTablet ? "32px 40px" : "36px 48px");
  const rightCardWidth = isMobile ? "calc(100% - 32px)" : (isTablet ? 400 : 440);
  const rightCardMargin = isMobile ? "16px" : "32px 48px 32px 0";
  const rightCardPadding = isMobile ? "26px 22px" : "40px 36px";
  const titleFontSize = isMobile ? 30 : 40;
  const headingFontSize = isMobile ? 19 : 21;
  const subheadFontSize = isMobile ? 14 : 15;

  return (
    <div style={{
      minHeight: "100vh", fontFamily: F,
      position: "relative", overflow: "hidden",
      display: "flex", alignItems: "stretch",
      flexDirection: isMobile ? "column" : "row",
      background: "linear-gradient(150deg, #07140e 0%, #0c2417 38%, #133a23 70%, #0c2417 100%)",
    }}>
      {/* Decorative ambient glow, no video for business — calmer, more corporate */}
      <div style={{ position: "absolute", top: "-10%", right: "-5%", width: "55%", height: "60%", background: "radial-gradient(circle, rgba(34,197,94,0.16), transparent 70%)", filter: "blur(40px)", zIndex: 0 }} />
      <div style={{ position: "absolute", bottom: "-15%", left: "-10%", width: "50%", height: "55%", background: "radial-gradient(circle, rgba(34,197,94,0.10), transparent 70%)", filter: "blur(50px)", zIndex: 0 }} />

      {/* ── LEFT: Headline ── */}
      <div style={{
        position: "relative", zIndex: 2,
        flex: 1, display: "flex", flexDirection: "column",
        justifyContent: "space-between",
        padding: leftPadding,
        minHeight: isMobile ? "auto" : "100vh",
      }}>
        <span onClick={() => navigate("/")} style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
          <AppLogo variant="light" />
        </span>

        <div style={{ marginTop: isMobile ? 40 : 0, marginBottom: isMobile ? 40 : 0 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 13px", borderRadius: 99, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)", marginBottom: 22 }}>
            <Icon name="Briefcase" size={13} color="#4ade80" strokeWidth={2} />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#d1fae5", fontFamily: F, letterSpacing: "0.02em" }}>JiraniApp for Business</span>
          </div>
          <h1 style={{ fontSize: titleFontSize, fontWeight: 700, color: "#fff", margin: "0 0 16px", lineHeight: 1.15, letterSpacing: "-0.025em", maxWidth: isMobile ? "100%" : 400 }}>
            Maintenance, handled<br/>across every property
          </h1>
          <p style={{ fontSize: subheadFontSize, color: "rgba(255,255,255,0.62)", margin: 0, maxWidth: isMobile ? "100%" : 340, lineHeight: 1.7, fontFamily: F }}>
            One account for your whole team. Book verified Pros, track every job, and pay one invoice at month end.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 30 }}>
            {[
              { icon: "Users", text: "Multi-user team access with role controls" },
              { icon: "FileText", text: "Single itemised invoice every month" },
              { icon: "Shield", text: "Business-tier verified professionals only" },
            ].map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(34,197,94,0.14)", border: "1px solid rgba(34,197,94,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name={f.icon} size={12} color="#4ade80" strokeWidth={2} />
                </div>
                <span style={{ fontSize: 13.5, color: "rgba(255,255,255,0.75)", fontFamily: F }}>{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p style={{ fontSize: isMobile ? 11 : 12, color: "rgba(255,255,255,0.5)", margin: "0 0 6px", fontFamily: F }}>Already registered your business?</p>
          <button onClick={() => openLogin()}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontSize: isMobile ? 14 : 15, fontWeight: 700, color: "#fff", padding: 0, fontFamily: F }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
            Sign in instead
            <Icon name="ArrowRight" size={16} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* ── RIGHT: Form card ── */}
      <div style={{
        position: "relative", zIndex: 2,
        width: rightCardWidth, flexShrink: 0,
        margin: rightCardMargin,
        background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(28px)",
        WebkitBackdropFilter: "blur(28px)",
        borderRadius: 24,
        padding: rightCardPadding,
        boxShadow: "0 32px 80px rgba(0,0,0,0.35), 0 2px 12px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.85)",
        display: "flex", flexDirection: "column", justifyContent: "center",
        border: "1px solid rgba(255,255,255,0.7)",
        alignSelf: isMobile ? "center" : "stretch",
        maxHeight: isMobile ? "none" : "calc(100vh - 64px)",
        overflowY: "auto",
      }}>

        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 18 }}>
          <AppLogo variant="dark" size="sm" />
        </div>

        {step === "details" && (
          <>
            <h2 style={{ fontSize: headingFontSize, fontWeight: 800, color: "#111827", margin: "0 0 4px", fontFamily: F, letterSpacing: "-0.02em" }}>
              Register your business
            </h2>
            <p style={{ fontSize: isMobile ? 12 : 13, color: "#6b7280", margin: "0 0 22px", fontFamily: F }}>
              Set up your company account to start booking for your team.
            </p>

            <form onSubmit={handleSendOtp}>
              <Field label="Company name" value={companyName} onChange={setCompanyName} placeholder="e.g. Acacia Properties Ltd" error={errors.companyName} />
              <Field label="Your name" value={contactName} onChange={setContactName} placeholder="Jane Wambui" error={errors.contactName} />

              <BusinessTypeGrid value={businessType} onChange={setBusinessType} isMobile={isMobile} />
              {errors.businessType && <p style={{ fontSize: 12, color: "#ef4444", margin: "-10px 0 14px 2px", fontFamily: F }}>{errors.businessType}</p>}

              <Field label="Phone number" type="tel" inputMode="tel" value={phone} onChange={setPhone} placeholder="+254 700 000 000" error={errors.phone} />
              <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="Create a password" error={errors.password} />

              {errors.general && (
                <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12, fontFamily: F }}>{errors.general}</p>
              )}

              <button type="submit" disabled={loading}
                style={{
                  width: "100%", background: "#111827", color: "#fff",
                  border: "none", borderRadius: 13, padding: isMobile ? "12px" : "14px",
                  fontSize: isMobile ? 14 : 15, fontWeight: 700, cursor: loading ? "default" : "pointer",
                  fontFamily: F, marginTop: 6, transition: "background 0.18s",
                  opacity: loading ? 0.8 : 1,
                  boxShadow: "0 8px 20px rgba(17,24,39,0.20)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.background = "#16a34a"; }}
                onMouseLeave={e => { if (!loading) e.currentTarget.style.background = "#111827"; }}>
                {loading ? "Sending code..." : <>Continue <Icon name="ArrowRight" size={16} color="#fff" strokeWidth={2.2} /></>}
              </button>
            </form>

            <p style={{ fontSize: isMobile ? 10 : 11, color: "#9ca3af", textAlign: "center", marginTop: 16, lineHeight: 1.6, fontFamily: F }}>
              By registering you agree to our Business Terms & Privacy Policy.
            </p>
          </>
        )}

        {step === "otp" && (
          <>
            <button onClick={() => setStep("details")} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: 13, fontFamily: F, padding: 0, marginBottom: 20 }}>
              <Icon name="ArrowLeft" size={14} strokeWidth={2.2} />
              Back
            </button>

            <div style={{
              width: 56, height: 56, borderRadius: 16,
              background: "linear-gradient(135deg, rgba(22,163,74,0.12), rgba(22,163,74,0.06))",
              border: "1.5px solid rgba(22,163,74,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 18px",
              boxShadow: "0 8px 20px rgba(22,163,74,0.10), inset 0 1px 0 rgba(255,255,255,0.5)",
            }}>
              <Icon name="MessageSquare" size={23} color="#16a34a" strokeWidth={1.7} />
            </div>

            <h2 style={{ fontSize: headingFontSize, fontWeight: 800, color: "#111827", margin: "0 0 6px", fontFamily: F, letterSpacing: "-0.02em", textAlign: "center" }}>
              Verify your number
            </h2>
            <p style={{ fontSize: isMobile ? 12 : 13, color: "#6b7280", margin: "0 0 26px", fontFamily: F, textAlign: "center", lineHeight: 1.6 }}>
              We sent a 6-digit code to<br/>
              <strong style={{ color: "#111827" }}>{formattedPhone}</strong>
            </p>

            <form onSubmit={handleVerifyOtp}>
              <OtpInput value={otp} onChange={setOtp} error={errors.otp} isMobile={isMobile} />

              <button type="submit" disabled={loading}
                style={{
                  width: "100%", background: "#16a34a", color: "#fff",
                  border: "none", borderRadius: 13, padding: isMobile ? "12px" : "14px",
                  fontSize: isMobile ? 14 : 15, fontWeight: 700, cursor: loading ? "default" : "pointer",
                  fontFamily: F, marginTop: 22, transition: "background 0.18s",
                  opacity: loading ? 0.8 : 1,
                  boxShadow: "0 8px 20px rgba(22,163,74,0.28)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.background = "#15803d"; }}
                onMouseLeave={e => { if (!loading) e.currentTarget.style.background = "#16a34a"; }}>
                {loading ? "Verifying..." : "Verify & create business account"}
              </button>
            </form>

            <div style={{ textAlign: "center", marginTop: 18 }}>
              <span style={{ fontSize: 12.5, color: "#9ca3af", fontFamily: F }}>
                Didn't get a code?{" "}
                {resendCooldown > 0 ? (
                  <span style={{ color: "#9ca3af" }}>Resend in {resendCooldown}s</span>
                ) : (
                  <span onClick={handleResend}
                    style={{ color: "#16a34a", fontWeight: 700, cursor: "pointer" }}
                    onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                    onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}>
                    Resend code
                  </span>
                )}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}